import { ForwardedRef, forwardRef, useImperativeHandle } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Stack,
    styled,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import useConfirmDialog from '../../hooks/dialogs/useConfirmDialog';
import useCommentTemplatesSWR from '../../hooks/commentTemplates/useCommentTemplatesSWR';
import useCommentTemplates from '../../hooks/commentTemplates/useCommentTemplates';
import { TableLoadingLayout } from '../layouts/LoadingLayouts/TableLoadingLayout';
import { ErrorTypography } from '../styled/ErrorTypography';
import {
    CommentTemplateProps,
    CommentTemplateRefProps,
} from '../../models/CommentTemplate';

const StyledLink = styled(Typography)(() => ({
    cursor: 'pointer',
    color: '#007cb0',
    paddingRight: '5px',
    ':hover': {
        textDecoration: 'underline',
    },
}));

// eslint-disable-next-line react/display-name
export const CommentTemplate = forwardRef(
    (
        props: CommentTemplateProps,
        ref: ForwardedRef<CommentTemplateRefProps>
    ) => {
        const { handleCommentSelect, title } = props;
        const { t } = useTranslation('quotations', {
            keyPrefix: 'QUOTATIONS.QUOTATION_EDITOR.COMMENT_TEMPLATE',
        });
        const confirmDialog = useConfirmDialog();
        const { comments, isLoading, isError, forceRefresh } =
            useCommentTemplatesSWR();
        const commentTemplates = useCommentTemplates();

        useImperativeHandle(ref, () => ({
            refresh() {
                forceRefresh().then();
            },
        }));

        const deleteComment = (id: number) => {
            commentTemplates.deleteCommentTemplate(id).then(() => {
                forceRefresh().then();
            });
        };

        const handleDelete = (id: number) => {
            confirmDialog.showDialog({
                title: t('DELETE_DIALOG.TITLE'),
                content: t('DELETE_DIALOG.CONTENT'),
                confirmText: t('DELETE_DIALOG.CONFIRM'),
                cancelText: t('DELETE_DIALOG.CANCEL'),
                onConfirm: () => deleteComment(id),
            });
        };

        return (
            <Stack>
                <Typography variant="small" sx={{ marginBottom: '12px' }}>
                    {title ?? t('TITLE')}
                </Typography>

                {isError && <ErrorTypography errorCode={''} />}

                {isLoading && <TableLoadingLayout />}

                {!isLoading && comments && (
                    <Box
                        sx={{
                            maxHeight: '200px',
                            overflow: 'auto',
                        }}
                    >
                        {comments.length == 0 && (
                            <Box>
                                <Typography>{t('NO_RESULT')}</Typography>
                            </Box>
                        )}
                        {comments.length > 0 &&
                            comments.map((elem) => (
                                <Accordion variant="outlined" key={elem.id}>
                                    <AccordionSummary
                                        sx={{ height: '48px' }}
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls="panel1a-content"
                                        id="panel1a-header"
                                    >
                                        <Stack
                                            flexDirection="row"
                                            width="95%"
                                            justifyContent="space-between"
                                        >
                                            <Typography>
                                                {elem.title}
                                            </Typography>

                                            <StyledLink
                                                onClick={(e) => {
                                                    e.stopPropagation(),
                                                        handleCommentSelect(
                                                            elem.note
                                                        );
                                                }}
                                            >
                                                {t('ADD')}
                                            </StyledLink>
                                        </Stack>
                                    </AccordionSummary>

                                    <AccordionDetails>
                                        <Typography>{elem.note}</Typography>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                    </Box>
                )}
            </Stack>
        );
    }
);
